import type { ReactNode } from 'react';
import { CircleDashed, EyeClosed, Monitor, Search, Undo2 } from 'lucide-react';
import { maybe } from '@passionware/monads';
import { Button } from 'v5/design-system/Button';
import {
  InputGroup2,
  InputGroup2Input,
  InputGroup2Slot
} from 'v5/design-system/InputGroup2';
import {
  ListBox,
  ListBoxHeader,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import {
  FeatureFlagConfig,
  FeatureFlagValueConfig
} from 'v5/services/front/FeatureManagementService/types';
import { getEffectiveBooleanValue } from '../featureFlagBulkActions';

export function FeatureFlagDetailSearch({
  value,
  onChange,
  placeholder,
  'aria-label': ariaLabel
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  'aria-label': string;
}) {
  return (
    <InputGroup2 size="sm" className="mt-3">
      <InputGroup2Slot>
        <Search className="size-4 text-fg-muted" />
      </InputGroup2Slot>
      <InputGroup2Input
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    </InputGroup2>
  );
}

/**
 * Visual-only switch. Do not use DS Switch here: it is inside FormDialogContent's
 * <form>, and Radix Switch's hidden bubble input fires a click on controlled
 * updates that would re-trigger the row onClick → infinite toggle loop.
 */
export function SwitchIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent',
        checked ? 'bg-bg-emphasis' : 'bg-bg-inset'
      )}
    >
      <span
        className={cn(
          'block h-5 w-5 rounded-full bg-bg-default transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </span>
  );
}

export function EnumFlagValue({
  value,
  onChange
}: {
  value: Extract<FeatureFlagValueConfig<boolean>, { type: 'enum' }>;
  onChange: (
    newValue: Extract<
      FeatureFlagValueConfig<boolean>,
      { type: 'enum' }
    >['current']
  ) => void;
}) {
  return (
    <div
      className="min-w-0 max-w-48"
      onClick={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
    >
      <Select value={value.current ?? undefined} onValueChange={onChange}>
        <SelectTrigger size="sm" className="w-full min-w-0">
          <SelectValue placeholder={String(value.defaultValue)} />
        </SelectTrigger>
        <SelectContent>
          {value.options.map(option => (
            <SelectItem size="lg" key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export type FeatureFlagToggleItem = {
  id: string;
  label: string;
  leadingIcon: ReactNode;
  secondary?: string;
  config: FeatureFlagConfig;
  onChange: (newValue: FeatureFlagValueConfig<false>['current']) => void;
  extra?: ReactNode;
};

type SupportedToggleItem = FeatureFlagToggleItem & {
  config: Extract<FeatureFlagConfig, { supported: true }>;
};

export function FeatureFlagToggleListBox({
  items,
  header,
  ariaLabel
}: {
  items: FeatureFlagToggleItem[];
  header?: ReactNode;
  ariaLabel: string;
}) {
  const supported = items.filter(
    (item): item is SupportedToggleItem => item.config.supported
  );
  if (supported.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-md border border-border-muted/60 bg-bg-default/60">
      {header}
      <ListBox
        aria-label={ariaLabel}
        selectionMode="none"
        onAction={key => {
          const item = supported.find(entry => entry.id === String(key));
          if (!item) {
            return;
          }
          const value = item.config.value as FeatureFlagValueConfig<boolean>;
          if (value.type !== 'boolean') {
            return;
          }
          item.onChange(getEffectiveBooleanValue(item.config) !== true);
        }}
        className="divide-border-muted/60"
      >
        {supported.map(item => {
          const value = item.config.value as FeatureFlagValueConfig<boolean>;
          const isBoolean = value.type === 'boolean';
          const checked = isBoolean
            ? getEffectiveBooleanValue(item.config) === true
            : false;

          return (
            <ListBoxItem
              key={item.id}
              id={item.id}
              textValue={
                item.secondary ? `${item.label}. ${item.secondary}` : item.label
              }
              aria-label={
                isBoolean
                  ? `${item.label}, ${checked ? 'on' : 'off'}`
                  : item.label
              }
              className="gap-2.5 px-3 py-2.5 last:rounded-b-md"
            >
              <ListBoxItemContent
                showCheckmark={false}
                icon={item.leadingIcon}
                primary={item.label}
                secondary={item.secondary}
                extra={
                  <span className="flex shrink-0 items-center gap-1">
                    {item.extra}
                    {isBoolean ? (
                      <SwitchIndicator checked={checked} />
                    ) : (
                      <EnumFlagValue value={value} onChange={item.onChange} />
                    )}
                  </span>
                }
              />
            </ListBoxItem>
          );
        })}
      </ListBox>
    </div>
  );
}

export function featureFlagBulkStatus(state: {
  allOn: boolean;
  allOff: boolean;
  onCount: number;
  totalCount: number;
  emptyLabel?: string;
  /** Noun used in status copy, e.g. "scope" or "flag". */
  unit?: string;
}): string {
  const unit = state.unit ?? 'item';
  const units = `${unit}s`;
  if (state.totalCount === 0) {
    return state.emptyLabel ?? `Apply to all ${units}`;
  }
  if (state.allOn) {
    return `On in every ${unit}`;
  }
  if (state.allOff) {
    return `Off in every ${unit}`;
  }
  return `On in ${state.onCount} of ${state.totalCount} ${units}`;
}

export function FeatureFlagBulkBar({
  bulkState,
  onSetAll,
  statusLabel,
  groupAriaLabel
}: {
  bulkState: {
    allOn: boolean;
    allOff: boolean;
    onCount: number;
    totalCount: number;
  };
  onSetAll: (enabled: boolean) => void;
  statusLabel: string;
  groupAriaLabel: string;
}) {
  return (
    <ListBoxHeader className="gap-3 border-border-muted/60 bg-bg-subtle/50 px-3 py-2">
      <p className="min-w-0 truncate text-sm text-fg-muted">{statusLabel}</p>
      <div
        role="group"
        aria-label={groupAriaLabel}
        className="relative inline-flex shrink-0 rounded-full p-0.5"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full bg-bg-inset mix-blend-multiply"
        />
        <Button
          type="button"
          variant={bulkState.allOff ? 'secondary' : 'ghost'}
          size="xs"
          shape="rounded"
          aria-pressed={bulkState.allOff}
          className="relative border-transparent"
          onClick={() => {
            if (!bulkState.allOff) {
              onSetAll(false);
            }
          }}
        >
          All off
        </Button>
        <Button
          type="button"
          variant={bulkState.allOn ? 'secondary' : 'ghost'}
          size="xs"
          shape="rounded"
          aria-pressed={bulkState.allOn}
          className="relative border-transparent"
          onClick={() => {
            if (!bulkState.allOn) {
              onSetAll(true);
            }
          }}
        >
          All on
        </Button>
      </div>
    </ListBoxHeader>
  );
}

export function FrontendOverrideControl({
  config,
  onChange,
  onClear
}: {
  config: FeatureFlagConfig;
  onChange: (newValue: FeatureFlagValueConfig<boolean>['current']) => void;
  onClear: () => void;
}) {
  if (!config.supported) {
    return null;
  }

  function getCanBeReset() {
    if (!config.supported) {
      return false;
    }
    if (config.optional) {
      return config.value.current !== null;
    }
    return config.value.current !== config.value.defaultValue;
  }

  const canBeReset = getCanBeReset();
  const value = config.value as FeatureFlagValueConfig<boolean>;
  const isBoolean = value.type === 'boolean';
  const checked =
    isBoolean && maybe.getOrElse(value.current, value.defaultValue) === true;

  const toggleBoolean = () => {
    if (!isBoolean) {
      return;
    }
    if (config.optional && checked) {
      onClear();
      return;
    }
    onChange(!checked);
  };

  return (
    <div
      role={isBoolean ? 'switch' : undefined}
      aria-checked={isBoolean ? checked : undefined}
      aria-label={isBoolean ? 'Local override' : undefined}
      tabIndex={isBoolean ? 0 : undefined}
      onClick={isBoolean ? toggleBoolean : undefined}
      onKeyDown={
        isBoolean
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleBoolean();
              }
            }
          : undefined
      }
      className={cn(
        'flex items-center justify-between gap-3 rounded-md px-3 py-2.5',
        isBoolean &&
          'cursor-pointer transition-colors hocus:bg-bg-subtle focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-300 focus-visible:ring-inset'
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Monitor className="size-4 shrink-0 text-fg-muted" aria-hidden />
        <span className="truncate text-sm font-medium" aria-hidden={isBoolean}>
          Local override
        </span>
      </div>
      <div className="flex items-center gap-1">
        {isBoolean ? (
          <div className={cn(value.current === null && 'opacity-50')}>
            <SwitchIndicator checked={checked} />
          </div>
        ) : (
          <EnumFlagValue value={value} onChange={onChange} />
        )}
        <SimpleTooltip
          title={
            canBeReset
              ? config.optional
                ? 'Unset the optional value'
                : 'Reset to default value'
              : config.optional
                ? 'This optional value is not set'
                : 'This is required and has a default value'
          }
        >
          <Button
            variant="ghost"
            className="shrink-0"
            layout="icon"
            size="md"
            onClick={event => {
              event.stopPropagation();
              onClear();
            }}
            visuallyDisabled={!canBeReset}
          >
            {canBeReset ? (
              <Undo2 className="size-4" />
            ) : config.optional ? (
              <EyeClosed className="size-4" />
            ) : (
              <CircleDashed className="size-4" />
            )}
          </Button>
        </SimpleTooltip>
      </div>
    </div>
  );
}
